import { useId, useRef } from "react";
import type { ReactElement, ReactNode } from "react";
import { createPortal } from "react-dom";

import { useTranslation } from "../../i18n";
import { Button } from "../atoms/Button";
import { Icon } from "../atoms/Icon";
import { preferredFocus, useModalFocus } from "../useModalFocus";
import styles from "./Dialog.module.css";

export interface DialogProps {
  readonly open: boolean;
  /** The visible heading, which also names the dialog (from `t()`). */
  readonly title: string;
  /** Called on the close button, on Escape and on a press on the scrim. */
  readonly onClose: () => void;
  /** The action row under a hairline (the mockup's `.dlg-foot`). */
  readonly footer?: ReactNode;
  /** `md` is the mockup's 560px dialog; `lg` the 600px command palette. */
  readonly size?: "md" | "lg" | undefined;
  readonly children: ReactNode;
}

/**
 * A modal dialog (the mockup's `.scrim` + `.dialog`): head with the title and a close button, body,
 * optional footer. The focus contract is `useModalFocus`: in on open (`data-autofocus`, else the first
 * control after the close button), trapped while open, back to the opener on close; Escape closes.
 * A press on the scrim closes too. It is portalled to the body, above the page and every popover.
 */
export function Dialog({ open, title, onClose, footer, size = "md", children }: DialogProps): ReactElement | null {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useModalFocus(open, dialogRef, onClose, preferredFocus);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.scrim}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={[styles.dialog, styles[size]].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={styles.head}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <span className={styles.close} data-close="">
            <Button variant="ghost" iconOnly label={t("common.action.close")} onClick={onClose}>
              <Icon name="close" />
            </Button>
          </span>
        </div>
        <div className={styles.body}>{children}</div>
        {footer === undefined ? null : <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
